import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, FocusData } from '../services/api';
import styles from './FocusWidget.module.css';

export default function FocusWidget() {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<FocusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await dashboardApi.focus(token);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function getUrgencyClass(days: number): string {
    if (days < 0) return styles.overdue;
    if (days < 3) return styles.critical;
    if (days < 7) return styles.warning;
    return styles.normal;
  }

  function getUrgencyIcon(days: number): string {
    if (days < 0) return '💀';
    if (days < 3) return '🚨';
    if (days < 7) return '⚠️';
    return '📅';
  }

  function formatDaysRemaining(days: number): string {
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `${days}d left`;
  }

  function handleDeadlineClick(sourceType: string) {
    if (sourceType === 'pipeline') {
      navigate('/pipeline');
    } else {
      navigate('/tasks');
    }
  }

  if (loading) {
    return (
      <div className={styles.widget}>
        <h2>Focus</h2>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.widget}>
        <h2>Focus</h2>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={styles.widget}>
      <h2>Focus</h2>

      {/* Urgent Deadlines */}
      {data.urgentDeadlines.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Upcoming Deadlines</h3>
          <div className={styles.deadlines}>
            {data.urgentDeadlines.slice(0, 5).map((item, idx) => (
              <div
                key={idx}
                className={`${styles.deadlineItem} ${getUrgencyClass(item.daysRemaining)}`}
                onClick={() => handleDeadlineClick(item.sourceType)}
              >
                <span className={styles.urgencyIcon}>{getUrgencyIcon(item.daysRemaining)}</span>
                <div className={styles.deadlineContent}>
                  <span className={styles.deadlineTitle}>{item.title}</span>
                  <span className={styles.deadlineMeta}>
                    {formatDaysRemaining(item.daysRemaining)} ({item.dueDate})
                  </span>
                </div>
                <span className={styles.sourceTag}>{item.sourceType}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social Status */}
      <div className={styles.section}>
        <div
          className={styles.socialStatus}
          onClick={() => navigate('/social')}
        >
          <div className={styles.socialProgress}>
            <div className={styles.socialLabel}>
              <span>Social Posts</span>
              <span className={styles.weekId}>{data.social.weekId}</span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${(data.social.postsCompleted / data.social.totalPlatforms) * 100}%` }}
              />
            </div>
            <span className={styles.socialCount}>
              {data.social.postsCompleted}/{data.social.totalPlatforms} posted
            </span>
          </div>
          {data.streak.current > 0 && (
            <div className={styles.streak}>
              <span className={styles.streakIcon}>🔥</span>
              <span className={styles.streakNum}>{data.streak.current}</span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Counts */}
      <div className={styles.counts}>
        <div className={styles.countItem} onClick={() => navigate('/tasks')}>
          <span className={styles.countNum}>{data.counts.tasksInProgress}</span>
          <span className={styles.countLabel}>tasks in progress</span>
        </div>
        <div className={styles.countItem} onClick={() => navigate('/pipeline')}>
          <span className={styles.countNum}>{data.counts.activePipelineItems}</span>
          <span className={styles.countLabel}>active deals</span>
        </div>
        <div className={styles.countItem} onClick={() => navigate('/pipeline')}>
          <span className={styles.countNum}>{data.counts.ideasInPipeline}</span>
          <span className={styles.countLabel}>ideas</span>
        </div>
      </div>

      {/* Empty state */}
      {data.urgentDeadlines.length === 0 && data.social.postsCompleted === data.social.totalPlatforms && (
        <div className={styles.allClear}>
          <span>✨</span> All caught up! No urgent items.
        </div>
      )}
    </div>
  );
}
